import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Meta, MetaDefinition } from '@angular/platform-browser';
import { SITE_CONFIG_DATA } from './site-config.model';

const ROBOTS_DIRECTIVE = 'noindex, nofollow, noarchive, nosnippet';
const ROBOTS_META_TAGS: { selector: string; def: MetaDefinition }[] = [
    { selector: 'name="robots"', def: { name: 'robots', content: ROBOTS_DIRECTIVE } },
    { selector: 'name="googlebot"', def: { name: 'googlebot', content: ROBOTS_DIRECTIVE } },
    { selector: 'name="bingbot"', def: { name: 'bingbot', content: ROBOTS_DIRECTIVE } },
    { selector: 'http-equiv="X-Robots-Tag"', def: { 'http-equiv': 'X-Robots-Tag', content: ROBOTS_DIRECTIVE } },
];

@Component({
    selector: 's1-website-config-editor-page',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <main class="mx-auto max-w-4xl px-5 pb-16 pt-8 leading-relaxed">
            <header class="mb-8">
                <p class="mb-2 inline-block rounded-full bg-warning/20 px-2 py-0.5 text-xs uppercase tracking-wider text-warning">
                    Editor only page — not indexed, not followed by robots.
                </p>
                <h1 class="mb-2 text-3xl font-bold">Website Config — Editor Instructions</h1>
                <p class="text-secondary">
                    This page is not part of the public site. It documents the
                    <code class="rounded bg-white px-1 py-0.5 text-sm">globals/website-config</code> Storyblok story and
                    provides validation links for the JSON-LD content embedded in every page's
                    <code class="rounded bg-white px-1 py-0.5 text-sm">&lt;head&gt;</code>.
                </p>
            </header>

            <section class="mb-10">
                <h2 class="mb-3 border-b border-grey-dark pb-1 text-xl font-semibold">What this story controls</h2>
                <p class="mb-3">
                    The <code class="rounded bg-white px-1 py-0.5 text-sm">json_ld_organization</code> field on the
                    <code class="rounded bg-white px-1 py-0.5 text-sm">globals/website-config</code> story holds the
                    site-wide <code class="rounded bg-white px-1 py-0.5 text-sm">Organization</code> JSON-LD schema.
                    At build time, the value is fetched and saved to a JSON file shipped with the application.
                    At server-side render time, it is injected as a
                    <code class="rounded bg-white px-1 py-0.5 text-sm">&lt;script type="application/ld+json" data-schema="organization"&gt;</code>
                    element into the document head.
                </p>
                <p>Editor edits become live on the next deployment, not immediately on publish.</p>
            </section>

            @if (parseError()) {
                <section class="mb-8 rounded-lg border border-error bg-white p-5">
                    <h2 class="mb-2 text-xl font-semibold text-error">⚠ JSON-LD is not active</h2>
                    <p class="mb-3">
                        The Organization schema is <strong>not</strong> being injected into
                        <code class="rounded bg-grey-light px-1 py-0.5 text-sm">&lt;head&gt;</code> because the JSON in
                        Storyblok could not be parsed. Build did not fail; the site is serving without the Organization schema.
                    </p>
                    <p class="mb-2"><strong>Parser error:</strong></p>
                    <pre class="mb-3 overflow-x-auto rounded-md bg-secondary p-4 text-sm text-white"><code>{{ parseError() }}</code></pre>
                    <p>
                        Open the Storyblok story, paste the current JSON into
                        <a class="text-primary underline" href="https://validator.schema.org/" target="_blank" rel="noopener noreferrer">validator.schema.org</a>
                        to find the issue, fix it, republish, and trigger a rebuild.
                    </p>
                </section>
            }

            @if (fetchError()) {
                <section class="mb-8 rounded-lg border border-warning bg-white p-5">
                    <h2 class="mb-2 text-xl font-semibold text-warning">⚠ Data may be stale</h2>
                    <p class="mb-3">
                        The most recent build attempt could <strong>not fetch</strong> the latest version from Storyblok.
                        The site is currently serving <strong>last-good data</strong> from
                        <code class="rounded bg-grey-light px-1 py-0.5 text-sm">{{ lastUpdatedAt() || 'unknown' }}</code>.
                        Newer edits in Storyblok are <strong>not yet reflected</strong> in
                        <code class="rounded bg-grey-light px-1 py-0.5 text-sm">&lt;head&gt;</code>.
                    </p>
                    <p class="mb-2">
                        Last fetch attempt:
                        <code class="rounded bg-grey-light px-1 py-0.5 text-sm">{{ lastFetchAttemptAt() || 'unknown' }}</code>
                    </p>
                    <p class="mb-2"><strong>Fetch error:</strong></p>
                    <pre class="mb-3 overflow-x-auto rounded-md bg-secondary p-4 text-sm text-white"><code>{{ fetchError() }}</code></pre>
                    <p>
                        Common causes: story unpublished, Storyblok outage, access token rotated, network failure during build.
                        Investigate, then trigger a rebuild.
                    </p>
                </section>
            }

            <section class="mb-10">
                <h2 class="mb-3 border-b border-grey-dark pb-1 text-xl font-semibold">Current value</h2>
                @if (currentValue().length) {
                    <div class="mb-3 flex flex-wrap items-center gap-2 text-sm text-secondary">
                        <span>
                            Last updated:
                            <code class="rounded bg-white px-1 py-0.5 text-xs">{{ lastUpdatedAt() || 'unknown' }}</code>
                        </span>
                        @if (lastFetchAttemptAt() && lastFetchAttemptAt() !== lastUpdatedAt()) {
                            <span>
                                · Last fetch attempt:
                                <code class="rounded bg-white px-1 py-0.5 text-xs">{{ lastFetchAttemptAt() }}</code>
                            </span>
                        }
                        @if (parseError()) {
                            <span class="rounded-full bg-error/15 px-2 py-0.5 text-xs font-medium text-error">
                                Not active
                            </span>
                        } @else if (fetchError()) {
                            <span class="rounded-full bg-warning/20 px-2 py-0.5 text-xs font-medium text-warning">
                                Active (stale)
                            </span>
                        } @else {
                            <span class="rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success">
                                Active in &lt;head&gt;
                            </span>
                        }
                    </div>
                    <div class="mb-3 flex flex-wrap items-center gap-3">
                        <button type="button"
                                class="cursor-pointer rounded-md border px-3 py-1.5 text-sm transition-colors"
                                [class.border-success]="copied()"
                                [class.bg-success]="copied()"
                                [class.text-white]="copied()"
                                [class.border-grey-dark]="!copied()"
                                [class.bg-white]="!copied()"
                                [class.hover:bg-grey-light]="!copied()"
                                (click)="copy()">
                            {{ copied() ? '✓ Copied to clipboard' : 'Copy JSON' }}
                        </button>
                        @if (copyError()) {
                            <span class="text-sm text-error">{{ copyError() }}</span>
                        }
                    </div>
                    <pre class="overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-secondary p-4 text-sm text-white"><code>{{ currentValue() }}</code></pre>
                } @else {
                    <p class="border-l-4 border-warning bg-white px-4 py-3 text-warning">
                        No JSON-LD currently configured. The Organization schema is <strong>not</strong> being injected.
                        Edit the Storyblok story and trigger a rebuild to publish.
                    </p>
                }
            </section>

            <section class="mb-10">
                <h2 class="mb-3 border-b border-grey-dark pb-1 text-xl font-semibold">Validation flow</h2>
                <ol class="list-decimal space-y-5 pl-6">
                    <li>
                        <strong>Step 1 — Schema.org validator</strong>
                        <span class="ml-2 text-sm font-normal text-secondary">(do this BEFORE publishing)</span>
                        <p class="mb-2 mt-1">
                            Copy your JSON, click below to open the validator, paste, run validation, fix any errors,
                            then come back and publish in Storyblok.
                        </p>
                        <a class="inline-block rounded-md bg-primary px-4 py-2 text-sm text-white hover:opacity-90"
                           href="https://validator.schema.org/" target="_blank" rel="noopener noreferrer">
                            Open validator.schema.org ↗
                        </a>
                    </li>
                    <li>
                        <strong>Step 2 — Google Rich Results test</strong>
                        <span class="ml-2 text-sm font-normal text-secondary">(do this AFTER stage deploy)</span>
                        <p class="mb-2 mt-1">
                            Once your changes are deployed to stage, paste the stage URL into Google's Rich Results test
                            to verify the Organization entity is detected with no errors.
                        </p>
                        <a class="inline-block rounded-md bg-primary px-4 py-2 text-sm text-white hover:opacity-90"
                           href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer">
                            Open Google Rich Results test ↗
                        </a>
                    </li>
                </ol>
            </section>

            @if (storyblokEditUrl()) {
                <section class="mb-10">
                    <h2 class="mb-3 border-b border-grey-dark pb-1 text-xl font-semibold">Edit in Storyblok</h2>
                    <a class="inline-block rounded-md bg-primary px-4 py-2 text-sm text-white hover:opacity-90"
                       [href]="storyblokEditUrl()" target="_blank" rel="noopener noreferrer">
                        Open story in Storyblok ↗
                    </a>
                </section>
            }

            <section class="mb-10">
                <h2 class="mb-3 border-b border-grey-dark pb-1 text-xl font-semibold">Status reference — what each state means</h2>
                <p class="mb-4">
                    After every deployment, the build pipeline fetches the latest Storyblok story and refreshes the JSON-LD
                    shipped with the application. The status shown above reflects what happened on the most recent build.
                </p>
                <div class="overflow-x-auto rounded-md border border-grey-dark bg-white">
                    <table class="w-full border-collapse text-left text-sm">
                        <thead class="bg-grey-light text-secondary">
                            <tr>
                                <th class="border-b border-grey-dark px-3 py-2.5 font-semibold">Status</th>
                                <th class="border-b border-grey-dark px-3 py-2.5 font-semibold">What happened</th>
                                <th class="border-b border-grey-dark px-3 py-2.5 font-semibold">Effect on public site</th>
                                <th class="border-b border-grey-dark px-3 py-2.5 font-semibold">What to do</th>
                            </tr>
                        </thead>
                        <tbody class="align-top">
                            <tr>
                                <td class="border-b border-grey-dark px-3 py-2.5">
                                    <span class="whitespace-nowrap rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success">
                                        Active in &lt;head&gt;
                                    </span>
                                </td>
                                <td class="border-b border-grey-dark px-3 py-2.5">Story fetched and parsed successfully.</td>
                                <td class="border-b border-grey-dark px-3 py-2.5">
                                    Organization JSON-LD is included in
                                    <code class="rounded bg-grey-light px-1 py-0.5 text-xs">&lt;head&gt;</code> on every page. Crawlers see it.
                                </td>
                                <td class="border-b border-grey-dark px-3 py-2.5">Nothing — this is the healthy state.</td>
                            </tr>
                            <tr>
                                <td class="border-b border-grey-dark px-3 py-2.5">
                                    <span class="whitespace-nowrap rounded-full bg-error/15 px-2 py-0.5 text-xs font-medium text-error">
                                        Not active
                                    </span>
                                </td>
                                <td class="border-b border-grey-dark px-3 py-2.5">
                                    Story was fetched but the JSON in
                                    <code class="rounded bg-grey-light px-1 py-0.5 text-xs">json_ld_organization</code> could not be parsed.
                                </td>
                                <td class="border-b border-grey-dark px-3 py-2.5">
                                    No JSON-LD <code class="rounded bg-grey-light px-1 py-0.5 text-xs">&lt;script&gt;</code>
                                    tag is injected. Site is otherwise healthy. SEO is missing the Organization schema.
                                </td>
                                <td class="border-b border-grey-dark px-3 py-2.5">
                                    Open the Storyblok story, paste the JSON into
                                    <a class="text-primary underline" href="https://validator.schema.org/" target="_blank" rel="noopener noreferrer">validator.schema.org</a>,
                                    fix the syntax error, republish, trigger a rebuild.
                                </td>
                            </tr>
                            <tr>
                                <td class="border-b border-grey-dark px-3 py-2.5">
                                    <span class="whitespace-nowrap rounded-full bg-warning/20 px-2 py-0.5 text-xs font-medium text-warning">
                                        Active (stale)
                                    </span>
                                </td>
                                <td class="border-b border-grey-dark px-3 py-2.5">
                                    Most recent build could not reach Storyblok (HTTP error, story unpublished, token rotated,
                                    network failure). Last-good data was preserved.
                                </td>
                                <td class="border-b border-grey-dark px-3 py-2.5">
                                    Organization JSON-LD is still in
                                    <code class="rounded bg-grey-light px-1 py-0.5 text-xs">&lt;head&gt;</code>, but it
                                    reflects an older version of the Storyblok story. Newer edits are not yet live.
                                </td>
                                <td class="border-b border-grey-dark px-3 py-2.5">
                                    Read the fetch error in the warning section above. Common fixes: re-publish the story,
                                    verify the access token, retry the build.
                                </td>
                            </tr>
                            <tr>
                                <td class="px-3 py-2.5">
                                    <em class="text-secondary">No value shown</em>
                                </td>
                                <td class="px-3 py-2.5">
                                    <code class="rounded bg-grey-light px-1 py-0.5 text-xs">json_ld_organization</code> is empty in Storyblok.
                                </td>
                                <td class="px-3 py-2.5">
                                    No JSON-LD <code class="rounded bg-grey-light px-1 py-0.5 text-xs">&lt;script&gt;</code> tag is injected.
                                </td>
                                <td class="px-3 py-2.5">Edit the story, paste validated JSON, republish, trigger a rebuild.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p class="mt-3 text-sm italic text-secondary">
                    Build never fails because of this story's content. If something goes wrong, the site keeps deploying
                    and the issue surfaces here on this page so you can fix it without involving developers.
                </p>
            </section>
        </main>
    `,
})
export class WebsiteConfigEditorPage implements OnInit, OnDestroy {
    private _route = inject(ActivatedRoute);
    private _meta = inject(Meta);
    private _config = inject(SITE_CONFIG_DATA, { optional: true });

    currentValue = signal<string>(
        this._config?.organization
            ? JSON.stringify(this._config.organization, null, 2)
            : ''
    );
    lastUpdatedAt = signal<string | undefined>(this._config?.lastUpdatedAt);
    lastFetchAttemptAt = signal<string | undefined>(this._config?.lastFetchAttemptAt);
    parseError = signal<string | null>(this._config?.parseError ?? null);
    fetchError = signal<string | null>(this._config?.fetchError ?? null);
    storyblokEditUrl = signal<string | undefined>(undefined);
    copied = signal<boolean>(false);
    copyError = signal<string | null>(null);
    private _resetTimer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        // Set robot exclusion meta tags as early as possible so they land in
        // the SSR-rendered HTML before crawlers can read it.
        for (const { selector, def } of ROBOTS_META_TAGS) {
            this._meta.updateTag(def, selector);
        }
    }

    ngOnInit(): void {
        const url = this._route.snapshot.data?.['storyblokEditUrl'];
        if (typeof url === 'string' && url.length) {
            this.storyblokEditUrl.set(url);
        }
    }

    ngOnDestroy(): void {
        // Remove the meta tags on navigation away so they don't leak onto other
        // pages during browser-side SPA navigation.
        for (const { selector } of ROBOTS_META_TAGS) {
            this._meta.removeTag(selector);
        }
    }

    async copy(): Promise<void> {
        const value = this.currentValue();
        if (!value) return;

        let success = false;

        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(value);
                success = true;
            } catch (err) {
                console.warn('[website-config-editor] navigator.clipboard.writeText failed, trying fallback', err);
            }
        }

        if (!success && typeof document !== 'undefined') {
            try {
                const textarea = document.createElement('textarea');
                textarea.value = value;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'fixed';
                textarea.style.top = '0';
                textarea.style.left = '0';
                textarea.style.width = '1px';
                textarea.style.height = '1px';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                textarea.setSelectionRange(0, value.length);
                success = document.execCommand('copy');
                document.body.removeChild(textarea);
            } catch (err) {
                console.warn('[website-config-editor] execCommand fallback failed', err);
            }
        }

        if (this._resetTimer) {
            clearTimeout(this._resetTimer);
        }

        if (success) {
            this.copyError.set(null);
            this.copied.set(true);
            this._resetTimer = setTimeout(() => this.copied.set(false), 2500);
        } else {
            this.copied.set(false);
            this.copyError.set('Copy failed — select and copy the JSON manually.');
            this._resetTimer = setTimeout(() => this.copyError.set(null), 5000);
        }
    }
}
